import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaStar, FaArrowRight, FaMoon } from 'react-icons/fa';
import axios from 'axios';
import '../styles/pages/Home.scss';
// Importando a imagem do pijama
import pijama1 from '../assets/images/pijama1.jpg';
import pijama2 from '../assets/images/pijama2.jpg';
import aboutImage from '../assets/images/about-luna.jpg';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Simular um delay para mostrar o loader
    const timer = setTimeout(() => {
      fetchFeaturedProducts();
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get('/api/products');
      // Limitar a 4 produtos para exibição em destaque
      setFeaturedProducts(response.data.slice(0, 4));
      setIsLoading(false);
    } catch (error) {
      console.error('Erro ao buscar produtos em destaque:', error);
      // Produtos de exemplo caso a API falhe
      setFeaturedProducts([
        {
          id: 1, 
          name: 'Pijama de Malha',
          price: 38.90, 
          images: [{ url: pijama1 }]
        },
        {
          id: 2, 
          name: 'Pijama de Malha',
          price: 38.90, 
          images: [{ url: pijama2 }]
        }
      ]);
      setIsLoading(false);
    }
  };
  
  // Animações
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };
  
  const moonVariants = {
    initial: { scale: 1, rotate: 0 },
    animate: { 
      scale: [1, 1.2, 1],
      rotate: [0, 10, -10, 0],
      transition: { 
        duration: 5, 
        repeat: Infinity,
        repeatType: "reverse" 
      }
    }
  };
  
  return (
    <div className="home-page">
      {/* Banner Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <motion.div 
              className="hero-text"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <h1>Conforto e Estilo para suas noites!</h1>
              <p>
                Descubra nossa coleção exclusiva de pijamas feitos com materiais de alta qualidade 
                para garantir seu conforto e bem-estar durante o descanso.
              </p>
              <Link to="/produtos" className="btn">
                Ver Coleção <FaArrowRight />
              </Link>
            </motion.div>
            
            <motion.div 
              className="hero-image"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <div className="moon-container">
                <motion.div
                  className="moon"
                  variants={moonVariants}
                  initial="initial"
                  animate="animate"
                >
                  <FaMoon />
                </motion.div>
                <div className="stars">
                  {[...Array(10)].map((_, i) => (
                    <motion.span 
                      key={i}
                      className="star"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0, 1, 0] }}
                      transition={{ 
                        duration: 2, 
                        repeat: Infinity, 
                        delay: i * 0.2,
                        repeatType: "reverse" 
                      }}
                    >
                      <FaStar />
                    </motion.span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Seção de Destaque */}
      <section className="featured">
        <div className="container">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Pijamas em Destaque
          </motion.h2>
          
          {isLoading ? (
            <div className="loading">Carregando produtos em destaque...</div>
          ) : (
            <motion.div 
              className="products-grid"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {featuredProducts.map(product => (
                <motion.div 
                  key={product.id} 
                  className="product-card"
                  variants={itemVariants}
                  whileHover={{ y: -10, transition: { duration: 0.2 } }}
                >
                  <Link to={`/produtos/${product.id}`}>
                    <div className="product-image">
                      <img 
                        src={product.images && product.images.length > 0 
                          ? product.images[0].url 
                          : 'https://via.placeholder.com/300x400?text=Sem+Imagem'} 
                        alt={product.name} 
                      />
                    </div>
                    <div className="product-info">
                      <h3>{product.name}</h3>
                      <p className="price">R$ {product.price.toFixed(2)}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
          
          <motion.div 
            className="view-all"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
          >
            <Link to="/produtos" className="btn btn-outline">
              Ver Todos os Produtos <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* Seção Sobre */}
      <section className="about-section">
        <div className="container">
          <motion.div 
            className="about-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="about-text">
              <h2>Sobre a Luna Pijamas</h2>
              <p>
                A Luna Pijamas nasceu do sonho de proporcionar noites mais confortáveis e 
                agradáveis. Nossos produtos são cuidadosamente criados com tecidos de alta 
                qualidade e designs exclusivos que combinam estilo e conforto.
              </p>
              <Link to="/sobre" className="btn btn-secondary">
                Conheça Nossa História
              </Link>
            </div>
            <div className="about-image">
              <motion.img 
                src={aboutImage} 
                alt="Sobre Luna Pijamas"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 